import React, { useEffect, useState } from "react";
import { Sparkles, MessageSquare, FileText, Lightbulb } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OPEN_ANNOUNCEMENT_EVENT } from "@/lib/announcement";

// Bump the id when publishing a new announcement so users see it once,
// even if they dismissed a previous one.
const ANNOUNCEMENT_ID = "wasmi-launch";
const STORAGE_KEY = `announcement-dismissed:${ANNOUNCEMENT_ID}`;

const isDismissed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    // Storage unavailable (private mode etc.) - the dialog will just show again.
  }
};

export const AnnouncementDialog: React.FC = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(() => !isDismissed());

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_ANNOUNCEMENT_EVENT, show);
    return () => window.removeEventListener(OPEN_ANNOUNCEMENT_EVENT, show);
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) markDismissed();
    setOpen(next);
  };

  const features = [
    { icon: MessageSquare, text: t("announcement.wasmi.feature_chat") },
    { icon: FileText, text: t("announcement.wasmi.feature_requests") },
    { icon: Lightbulb, text: t("announcement.wasmi.feature_ideas") },
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md overflow-hidden p-0">
        <div className="flex flex-col items-center gap-3 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-6 pt-8 pb-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
            <Sparkles className="h-7 w-7" />
          </div>
          <Badge variant="secondary">{t("common.coming_soon")}</Badge>
          <DialogHeader className="items-center space-y-2 text-center sm:text-center">
            <DialogTitle className="text-2xl">
              {t("announcement.wasmi.title")}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t("announcement.wasmi.description")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <ul className="space-y-3 px-6">
          {features.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
              {text}
            </li>
          ))}
        </ul>

        <DialogFooter className="px-6 pb-6">
          <Button className="w-full" onClick={() => handleOpenChange(false)}>
            {t("announcement.dismiss")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
